import React from 'react';
import { PolarArea } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend);

const StarterChart = ({ originalData, labels, ch }) => {
  //console.log("=== StarterChart Debug Info ===");
  //console.log("originalData received:", originalData);
  //console.log("labels received:", labels);
  //console.log("ch parameter:", ch);
  
  const maxRadius = 2;

  const labelOrder1 = ['mind', 'soul', 'strength', 'heart'];
  const labelOrder2 = ['strategic', 'visionary', 'performance','collaborative'];
  // const labelOrder = ['mind', 'soul', 'strength', 'heart'];
  const starterLabels = ['mind', 'soul', 'strength', 'heart'];
  const leadershipLabels = ['strategic', 'visionary', 'performance', 'collaborative'];
  // const leadershipLabels = ['strategic', 'performance', 'visionary', 'collaborative'];

  const starterColors = {
    mind: '#667F96',
    soul: '#00577D',
    strength: '#C99212',
    heart: '#B6111F',
  };

  const leadershipColors = {
    strategic: '#667F96',
    performance: '#C99212',
    visionary: '#00577D',
    collaborative: '#B6111F',
  };

  const isStarter = labels.every((label) => starterLabels.includes(label));
  const labelOrder = isStarter ? starterLabels : leadershipLabels;
  const colorMapping = isStarter ? starterColors : leadershipColors;

  const rearrangeDataAndLabels = (data, labels, order) => {
    
    const orderedData = [];
    const orderedLabels = [];

    order.forEach((label) => {
      const index = labels.indexOf(label);
      //console.log(`Looking for label '${label}' in labels, found at index: ${index}`);
      if (index !== -1) {
        orderedLabels.push(labels[index]);
        orderedData.push(data[index]);
        //console.log(`Added: label='${labels[index]}', data=${data[index]}`);
      }
    });

    //console.log("Final orderedLabels:", orderedLabels);
    //console.log("Final orderedData:", orderedData);
    return { orderedLabels, orderedData };
  };
  
  const adjustDataForClockwiseRendering = (data) => {
    return [data[2], data[3], data[1], data[0]];
  };
  
  // Rearrange data and labels
  const { orderedLabels, orderedData } = rearrangeDataAndLabels(originalData, labels, labelOrder);
  const adjustedData = orderedData
  //console.log("After rearrange - orderedLabels:",orderedLabels);
  //console.log("After rearrange - orderedData:",orderedData);
  //console.log("adjustedData (using orderedData):", adjustedData);

  const backgroundColors = orderedLabels.map((label) => colorMapping[label]);

  //console.log("Final backgroundColors:", backgroundColors);

  // Calculate radius based on percentage area
  const calculateRadius = (percentage) => Math.sqrt((percentage / 100) * Math.pow(maxRadius, 2));
  
  const data = {
    labels: orderedLabels,
    datasets: [
      {
        label: 'Distribution',
        data: orderedData.map((value) => calculateRadius(value)),
        backgroundColor: ['#C99212', '#B6111F', '#00577D', '#667F96'], // Back to original Figma colors
        borderColor: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
        borderWidth: 10,
        hoverBackgroundColor: ['#C99212', '#B6111F', '#00577D', '#667F96'], // Back to original Figma colors
        hoverBorderColor: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
        originalData: orderedData,
      },
    ],
  };

  //console.log("Final chart data object:", data);
  //console.log("Chart will render with:");
  //console.log("- Data values:", data.datasets[0].data);
  //console.log("- Background colors:", data.datasets[0].backgroundColor);
  //console.log("- Original data for percentages:", data.datasets[0].originalData);
  //console.log("- Labels:", data.labels);

  const options = {
    scales: {
      r: {
        startAngle: 8 * (Math.PI / 180),
        grid: {
          display: false,
        },
        ticks: {
          display: false,
          max: maxRadius,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const centerTextPlugin = {
    id: 'centerTextPlugin',
    afterDatasetsDraw: (chart) => {
      const { ctx, data } = chart;

      ctx.save();
      data.datasets[0].originalData?.forEach((value, index) => {
        const meta = chart.getDatasetMeta(0);
        const arc = meta.data[index];
        const centerPoint = arc.tooltipPosition();
        const text = `${value.toFixed(2)}%`;

        ctx.fillStyle = '#FFFFFF';
        ctx.font = value <= 10 ? '700 10px Lato' : '700 12px Lato'; 
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        if (value <= 15) {
          
          if(index == 0){
            centerPoint.y += -4;
            centerPoint.x += 4;
          }
          else if(index == 1){
            centerPoint.y += 4;
            centerPoint.x += 4;
          }
          else if (index == 2){
            centerPoint.y += 4;
            centerPoint.x += -4;
          }
          else{
            centerPoint.y += -4;
            centerPoint.x += -4;
          }
        }
        ctx.fillText(text, centerPoint.x, centerPoint.y);
      });
      ctx.restore();
    },
  };

  const cornerLabelsPlugin = {
    id: 'cornerLabelsPlugin',
    beforeDraw: (chart) => {
      const { ctx, chartArea, data } = chart;
      if (!chartArea || !data.labels) return;

      ctx.save();

      // Font and styling
      ctx.font = ch === 'mind' ? '700 18px Lato' : '700 18px Lato';
      ctx.fillStyle = '#343232';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const paddingX = ch === 'mind' ? 48 : 48; // Horizontal padding
      const paddingY = 10; // Vertical padding

      const topLeft = { x: chartArea.left + paddingX, y: chartArea.top + paddingY };
      const topRight = { x: chartArea.right - paddingX, y: chartArea.top + paddingY };
      const bottomLeft = { x: chartArea.left + paddingX, y: chartArea.bottom - paddingY };
      const bottomRight = { x: chartArea.right - paddingX, y: chartArea.bottom - paddingY };

      // Ensure there are exactly 4 labels
      const labels = data.labels.slice(0, 4);

      // Draw labels at the corners
      if (labels[0]) ctx.fillText(labels[0].charAt(0).toUpperCase() + labels[0].slice(1).toLowerCase(), topLeft.x, topLeft.y); // Top-left
      if (labels[1]) ctx.fillText(labels[1].charAt(0).toUpperCase() + labels[1].slice(1).toLowerCase(), bottomLeft.x, bottomLeft.y); // Top-right
      if (labels[2]) ctx.fillText(labels[2].charAt(0).toUpperCase() + labels[2].slice(1).toLowerCase(), topRight.x, topRight.y); // Bottom-left
      if (labels[3]) ctx.fillText(labels[3].charAt(0).toUpperCase() + labels[3].slice(1).toLowerCase(), bottomRight.x, bottomRight.y); // Bottom-right

      ctx.restore();
    },
  };

  return (
    <div style={{ 
      height: '310px',
      width: '350px',
      maxWidth: '100%', 
      maxHeight: '100%', 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center', }}>
      <PolarArea data={data} options={options} plugins={[centerTextPlugin, cornerLabelsPlugin]} />
    </div>
  );
};

export default StarterChart;
